import { EVENT_INTERACTION } from '@eventbrite/eds-event-card';
import { useCallback, useEffect, useRef } from 'react';
import {
    EventInteraction,
    EventInteractions,
    PLACE_EVENT_INTERACTION,
} from './placeEventInteraction';

export function usePlacementEventContextAggregator(
    placeElement: { current: HTMLElement | null },
    getPlaceInteractionInfo: (eventInteraction: EventInteraction) => any,
    enableListenInteractions = true,
) {
    const interactions = useRef<
        {
            id: string;
            action: EventInteractions;
            isPromoted: boolean;
            element: HTMLElement;
        }[]
    >([]);

    const getPlaceInfo = useRef(getPlaceInteractionInfo);

    useEffect(() => {
        getPlaceInfo.current = getPlaceInteractionInfo;
    }, [getPlaceInteractionInfo]);
    const logInteractions = useCallback(() => {
        interactions.current
            ?.filter(({ element }) => placeElement.current?.contains(element))
            .forEach(({ id, action, isPromoted }) => {
                const placeEventInteractionEvent = new CustomEvent(
                    PLACE_EVENT_INTERACTION,
                    {
                        detail: getPlaceInfo.current({
                            id,
                            action,
                            isPromoted,
                        }),
                    },
                );
                document.dispatchEvent(placeEventInteractionEvent);
            });
        interactions.current = [];
    }, [interactions, placeElement, getPlaceInfo]);

    const storeInteractions = useCallback(
        (e: InteractionCustomEvent) => {
            e.preventDefault();
            interactions.current.push({
                id: e.detail.id,
                action: e.detail.action,
                isPromoted: e.detail.isPromoted,
                element: e.srcElement,
            });
            placeElement.current && logInteractions();
        },
        [interactions, logInteractions, placeElement],
    );

    const willMount = useRef(true);

    useEffect(() => {
        if (willMount.current && enableListenInteractions) {
            document.addEventListener(EVENT_INTERACTION, storeInteractions);
        }
        willMount.current = false;
    }, [storeInteractions, enableListenInteractions]);

    useEffect(() => {
        logInteractions();
        return () =>
            document.removeEventListener(EVENT_INTERACTION, storeInteractions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

interface InteractionCustomEvent {
    preventDefault: () => void;
    detail: {
        id: string;
        action: EventInteractions;
        isPromoted: boolean;
    };
    srcElement: HTMLElement;
}
